import _ from 'lodash'
import type {
    Menu,
    Ingredient,
    Product,
    ProductGroup,
    Step,
    ReusableOrder
} from './types'
import type { EcommOrder, PurchasableItem } from 'shared/store/ecommerce/order'
import type {
    CacCatalog,
    CacProductCategory,
    ProductOption,
    ProductIngredient,
    MenuStep,
    CacOrder,
    CacOrderContent
} from 'shared/types'
import type { State } from './reducer'
import { sumProductFinalPrices } from 'shared/utils/ecommerce'

const typesBO = {
    PRODUCT: 'products',
    PRODUCT_GROUP: 'productGroups',
    MENU: 'menus',
    INGREDIENT: 'ingredients',
    ALLERGEN: 'allergens',
    CATEGORY: 'categories'
}

const types = {
    PRODUCT: 'PRODUCT',
    PRODUCT_GROUP: 'PRODUCT_GROUP',
    MENU: 'MENU',
    INGREDIENT: 'INGREDIENT'
}

const getCategoryFromBO = (
    state: State,
    categories?: Array<CacProductCategory>,
    categoryId?: string
) => {
    if (categories && categories.length > 0) {
        const idCategory = categoryId || categories[0].id
        return {
            categoryId: idCategory,
            categoryName: getCategoryNameById(state, idCategory)
        }
    } else {
        return {
            categoryId: categoryId || '',
            categoryName: ''
        }
    }
}

const getCategoryNameById = (state: State, idCategory: string): string => {
    const currentCategory =
        state.categoriesById && state.categoriesById[idCategory]
    return currentCategory ? currentCategory.name : ''
}

const getIngredientsFromProduct = (
    state: State,
    ingredients?: Array<ProductIngredient>,
    composable?: boolean
) => {
    if (ingredients && ingredients.length > 0) {
        const listIngredients = ingredients.map(
            ({
                maxQty,
                initialQty,
                canModify,
                minQty,
                ingredientDefaultId,
                ingredientIds,
                groupType
            }) =>
                getIngredientById(
                    state,
                    ingredientDefaultId,
                    maxQty,
                    minQty,
                    initialQty,
                    canModify,
                    ingredientIds,
                    groupType,
                    composable
                )
        )

        //  On peut avoir plusieurs fois le même ingrédient, donc on va regrouper les ingrédients par id,
        // vérifier si certains ont une longueur > 1, et si oui on ne le remet qu'une fois dans la liste
        // d'ingrédients et on update sa quantité max

        const sortedByIdIngredients = _.groupBy(listIngredients, 'id')

        const uniqueIngredientsWithMaxQuantityUpdated = Object.keys(
            sortedByIdIngredients
        ).map(key => {
            if (sortedByIdIngredients[key].length === 1) {
                return sortedByIdIngredients[key]
            } else {
                return [
                    {
                        ...sortedByIdIngredients[key][0],
                        quantityMax: sortedByIdIngredients[key].length
                    }
                ]
            }
        })

        const ingredientsFlat = uniqueIngredientsWithMaxQuantityUpdated.reduce(
            function(a, b) {
                return a.concat(b)
            }
        )

        const ingredientsCanModify = ingredientsFlat.filter(
            ingredient => ingredient && ingredient.canModify === true
        )

        // On trie la liste pour afficher les ingrédients du plus au moins cher
        return ingredientsCanModify.sort(function(a, b) {
            const priceA = (a && a.price) || 0
            const priceB = (b && b.price) || 0
            return priceB - priceA
        })
    }
}

const getOptionsFromProduct = (
    catalog?: CacCatalog,
    optionsBO?: Array<ProductOption>
) => {
    if (optionsBO && catalog) {
        const optionsProduct = optionsBO.filter(
            option => option.optionType === 1
        )

        const productsBO = catalog[typesBO.PRODUCT]
        if (productsBO) {
            return optionsProduct.map(optionProduct => {
                return productsBO
                    .filter(
                        option =>
                            optionProduct &&
                            optionProduct.productIds &&
                            optionProduct.productIds.some(
                                optionId => option.id === optionId
                            )
                    )
                    .map(option => {
                        const { id, name, available, active, image } = option
                        return {
                            id,
                            name,
                            image,
                            available,
                            active,
                            quantityMax: 1
                        }
                    })
            })
        }
    }
}

const getStepsFromMenu = (
    state: State,
    stepsBO?: Array<MenuStep>
): ?Array<Step> => {
    if (stepsBO && stepsBO.length > 0) {
        return stepsBO.map(stepBO => {
            const {
                name,
                productIds,
                productLIds,
                productXLIds,
                productSIds,
                defaultId,
                stepType
            } = stepBO
            let productsInStep = []
            let productsSInStep = []
            let productsLInStep = []
            let productsXLInStep = []

            if (productIds) {
                productsInStep = productIds
                    .map(productId => getProductById(state, productId))
                    .filter(product => product !== undefined)
            }
            if (productSIds) {
                productsSInStep = productSIds
                    .map(productSId => getProductById(state, productSId))
                    .filter(product => product !== undefined)
            }
            if (productLIds) {
                productsLInStep = productLIds
                    .map(productLId => getProductById(state, productLId))
                    .filter(product => product !== undefined)
            }
            if (productXLIds) {
                productsXLInStep = productXLIds
                    .map(productXLId => getProductById(state, productXLId))
                    .filter(product => product !== undefined)
            }
            return {
                //$FlowFixMe
                name: name.fr,
                defaultId,
                stepType,
                products: productsInStep,
                productsS: productsSInStep,
                productsL: productsLInStep,
                productsXL: productsXLInStep
            }
        })
    }
}

export const getMenuById = (
    state: State,
    idMenu: string,
    categoryId?: string,
    isSuggestion?: boolean
): ?Menu => {
    const menuInCatalog = state.menusById && state.menusById[idMenu]
    if (menuInCatalog) {
        const {
            id,
            name,
            routeId,
            image,
            price,
            priceS,
            priceL,
            priceXL,
            existsInS,
            existsInL,
            existsInXL,
            available,
            earnPoints,
            kidsMenu,
            steps,
            categories,
            customizable,
            suggestionsIds,
            maxQuantity,
            airshipTagInApp
        } = menuInCatalog
        const category = getCategoryFromBO(state, categories, categoryId)
        const suggestions = !isSuggestion
            ? suggestionsIds &&
              suggestionsIds.map(suggestionId =>
                  getProductById(state, suggestionId, undefined, true)
              )
            : undefined
        return {
            id,
            type: types.MENU,
            name,
            categoryName: category?.categoryName ?? '',
            categoryId: categoryId || (category?.categoryId ?? ''),
            existsInS,
            existsInL,
            existsInXL,
            routeId,
            image,
            price,
            priceS,
            priceL,
            priceXL,
            available,
            earnPoints,
            kidsMenu,
            steps: getStepsFromMenu(state, steps),
            // TODO: Map those fields
            hasSelectedAllergens: false,
            subCategoryId: '',
            customizable,
            suggestions,
            maxQuantity,
            airshipTagInApp
        }
    }
}

export const getProductGroupById = (
    state: State,
    idProductGroup: string,
    categoryId?: string
): ?ProductGroup => {
    const productGroupInCatalog =
        state.productGroupsById && state.productGroupsById[idProductGroup]
    if (productGroupInCatalog) {
        const {
            id,
            name,
            routeId,
            image,
            productIds,
            categories
        } = productGroupInCatalog
        const category = getCategoryFromBO(state, categories, categoryId)
        const products = productIds.map(productId =>
            getProductById(state, productId)
        )
        return {
            id,
            type: types.PRODUCT_GROUP,
            name: name,
            categoryName: category?.categoryName ?? '',
            categoryId: categoryId || (category?.categoryId ?? ''),
            routeId,
            image,
            products,
            price: 0,
            // TODO: Map those fields
            hasSelectedAllergens: false,
            subCategoryId: ''
        }
    }
}

export const getProductById = (
    state: State,
    idProduct: string,
    categoryId?: string,
    isSuggestion?: boolean
): ?Product => {
    const productInCatalog = state.productsById && state.productsById[idProduct]
    if (productInCatalog) {
        const {
            id,
            name,
            routeId,
            image,
            price,
            available,
            earnPoints,
            ingredients,
            options,
            categories,
            menuExtraPriceTTC,
            active,
            suggestionsIds,
            maxQuantity,
            hasIce,
            pickUpLater,
            airshipTagInApp,
            composable,
            configurable,
            allergens
        } = productInCatalog
        if (active) {
            const category = getCategoryFromBO(state, categories, categoryId)
            const suggestions = !isSuggestion
                ? suggestionsIds &&
                  suggestionsIds.map(suggestionId =>
                      getProductById(state, suggestionId, undefined, true)
                  )
                : undefined
            return {
                id,
                type: types.PRODUCT,
                name,
                routeId,
                categoryName: category?.categoryName ?? '',
                categoryId: categoryId || (category?.categoryId ?? ''),
                image,
                price,
                available,
                earnPoints,
                ingredients: getIngredientsFromProduct(
                    state,
                    ingredients,
                    composable
                ),
                options: getOptionsFromProduct(state.catalog, options),
                hasIce,
                hasPickUpLater: pickUpLater,
                // TODO: Map those fields
                hasSelectedAllergens: false,
                subCategoryId: '',
                menuExtraPriceTTC,
                suggestions,
                maxQuantity,
                airshipTagInApp,
                allergens,
                composable,
                configurable
            }
        }
    }
}

export const getIngredientsFromListIngredientsIds = (
    state: State,
    ingredientsGroup?: Array<Ingredient>
): Array<any> => {
    if (!!ingredientsGroup && ingredientsGroup.length > 0) {
        const ingredients = ingredientsGroup
            .filter(
                group =>
                    !!group.listIngredientIds &&
                    group.listIngredientIds.length !== 0
            )
            .map(group => {
                // to reassure flow
                if (!group.listIngredientIds) {
                    return {
                        ingredients: [],
                        groupType: 'NONE'
                    }
                }
                const ingredientByGroup = group.listIngredientIds
                    .map(
                        id => state.ingredientsById && state.ingredientsById[id]
                    )
                    .filter(ingr => !!ingr && !!ingr.active && !!ingr.available)
                return {
                    ingredients: ingredientByGroup,
                    groupType: group.groupType
                }
            })
        return ingredients
    }
    return []
}

export const getIngredientById = (
    state: State,
    ingredientDefaultId?: string,
    maxQty?: number,
    minQty?: number,
    initialQty?: number,
    canModify?: boolean,
    listIngredientIds?: Array<string>,
    groupType?: 'NONE' | 'ICE_CREAM' | 'GLAZING' | 'TOPPING',
    composable?: boolean = false
): ?Ingredient => {
    if (ingredientDefaultId) {
        const ingredientInCatalog =
            state.ingredientsById && state.ingredientsById[ingredientDefaultId]

        if (ingredientInCatalog) {
            const {
                id,
                name,
                image,
                price,
                available,
                description,
                active
            } = ingredientInCatalog
            // active || composable because reboot catalog gives a listOfIngredients like "nappage" and "topping"
            // "nappage" have a listOfIngredientsIds and the defaultId, the defaultId can be active=false but we need the list of ID with it because the product is Composable.
            if (active || composable) {
                return {
                    id,
                    name,
                    active,
                    image,
                    price,
                    available,
                    description,
                    quantityMax: maxQty,
                    quantityMin: minQty,
                    initialQty,
                    canModify,
                    ingredientDefaultId,
                    listIngredientIds,
                    groupType
                }
            }
        }
    }
}

const cacOrderContentToPurchasable = (
    state?: State,
    orderContent: CacOrderContent
): PurchasableItem => {
    const {
        freeItems,
        promotions,
        recipe,
        subContent,
        id,
        ...rest
    } = orderContent
    if (subContent) {
        const menuCC = state && state.menusById && state.menusById[id || '']
        const steps = subContent.map(step =>
            //$FlowFixMe
            cacOrderContentToPurchasable(state, step)
        )
        if (menuCC && state) {
            const { categories, earnPoints, available, menuSize } = menuCC
            const { categoryId, categoryName } = getCategoryFromBO(
                state,
                categories
            )
            return {
                ...rest,
                id: id || '',
                type: types.MENU,
                categoryId,
                categoryName,
                earnPoints,
                available,
                menuSize,
                steps,
                promotions,
                canBeCustomized: true
            }
        } else {
            return {
                ...rest,
                id: id || '',
                type: types.MENU,
                categoryId: '',
                categoryName: '',
                available: !Boolean(state),
                steps,
                promotions,
                canBeCustomized: true
            }
        }
    } else {
        const productCC =
            state && state.productsById && state.productsById[id || '']
        let options
        let customizedIngredients
        if (freeItems) {
            options = freeItems.map((freeItem, index) => {
                const freeItemCC =
                    state &&
                    state.productsById &&
                    freeItem &&
                    freeItem.id &&
                    state.productsById[freeItem.id]
                return {
                    id: freeItem.id,
                    quantity: freeItem.quantity,
                    label: freeItem.label,
                    available: freeItemCC
                        ? freeItemCC.available
                        : !Boolean(state),
                    fromChoice: index
                }
            })
        }
        if (recipe) {
            customizedIngredients = recipe.map(ingredient => {
                const {
                    initial,
                    selected,
                    label,
                    initialQuantity,
                    quantity,
                    price
                } = ingredient
                const ingredientCC =
                    state &&
                    selected &&
                    state.ingredientsById &&
                    state.ingredientsById[selected]
                return {
                    initial,
                    selected,
                    label,
                    initialQuantity,
                    quantity,
                    price,
                    available: ingredientCC
                        ? ingredientCC.available
                        : !Boolean(state)
                }
            })
        }
        if (productCC && state) {
            const { categories, earnPoints, available } = productCC
            const { categoryId, categoryName } = getCategoryFromBO(
                state,
                categories
            )
            let canBeCustomized = false
            if (
                (productCC.options && productCC.options.length > 0) ||
                (productCC.ingredients && productCC.ingredients.length > 0)
            ) {
                canBeCustomized = true
            }
            //$FlowFixMe
            return {
                ...rest,
                id: id || '',
                type: types.PRODUCT,
                categoryId,
                categoryName,
                earnPoints,
                options,
                customizedIngredients,
                available,
                promotions,
                canBeCustomized
            }
        } else {
            return {
                ...rest,
                id: id || '',
                type: types.PRODUCT,
                categoryId: '',
                categoryName: '',
                available: !Boolean(state),
                options,
                customizedIngredients,
                promotions
            }
        }
    }
}

export const ecommOrderToCart = (
    order: EcommOrder,
    state?: State
): ?Array<PurchasableItem> => {
    if (order.content && order.content.length > 0) {
        return order.content.map(content =>
            cacOrderContentToPurchasable(state, content)
        )
    }
}

const purchasableFilterForReusableOrder = (
    purchasables: Array<PurchasableItem>
) => {
    return purchasables.map(purchasable => {
        let purchasableReturn = { ...purchasable }
        if (purchasable.promotions && purchasable.promotions.length > 0) {
            purchasableReturn = { ...purchasable, available: false }
        } else {
            if (purchasable.type === types.MENU && purchasable.steps) {
                if (purchasable.steps.find(step => step.available === false)) {
                    purchasableReturn = { ...purchasable, available: false }
                }
                purchasable.steps &&
                    purchasable.steps.forEach(step => {
                        if (
                            (step.customizedIngredients &&
                                step.customizedIngredients.find(
                                    ingredient => ingredient.available === false
                                )) ||
                            (step.options &&
                                step.options.find(
                                    option => option.available === false
                                ))
                        ) {
                            purchasableReturn = {
                                ...purchasable,
                                available: false
                            }
                        }
                    })
            } else if (purchasable.type === types.PRODUCT) {
                if (
                    (purchasable.customizedIngredients &&
                        purchasable.customizedIngredients.find(
                            ingredient => ingredient.available === false
                        )) ||
                    (purchasable.options &&
                        purchasable.options.find(
                            option => option.available === false
                        ))
                ) {
                    purchasableReturn = { ...purchasable, available: false }
                }
            }
        }
        return purchasableReturn
    })
}

export const getReusableOrder = (
    order: CacOrder,
    state: State
): ReusableOrder => {
    const contentToPurchasable =
        order.content &&
        order.content.map(contentItems =>
            cacOrderContentToPurchasable(state, contentItems)
        )
    const content =
        contentToPurchasable &&
        purchasableFilterForReusableOrder(contentToPurchasable)
    const contentAvailable = content && content.filter(item => item.available)
    const price = contentAvailable ? sumProductFinalPrices(contentAvailable) : 0
    return {
        price,
        content,
        contentAvailable
    }
}
